import React, { FC, useEffect, useRef, useState } from 'react';
import { classes, style, stylesheet } from 'typestyle';
import {
  Button,
  ButtonsRow,
  Gray100,
  Loader,
  Select,
  typeCompact14Medium,
  WindowBody,
  WindowHeader,
} from '@traceair/tds';
import { DocType, FileHash, PilotDoc } from '../../../../types';
import { useAppStore } from '../../../../store/appStore';
import { trackAction } from '../../../../libs/analytics/ActionTracking';
import DocDropbox from './DocDropbox';
import DocDatePicker from './DocDatePicker';
import { useDocumentsStore } from '../../../../store/documentsStore';
import { calcHash } from '../../../../libs/file';
import getPilotApiService, { FileUploadContext } from '../../../../api_services/PilotApiService';
import FilePreview from './FilePreview';
import { format } from 'date-fns';

type NewFileState = {
  file: File;
  exchangeUrl?: string;
  isUploading?: boolean;
};

type EditDocPopupProps = {
  docType: DocType;
  document?: PilotDoc;
  projectId?: string;
};

const EditDocPopup: FC<EditDocPopupProps> = ({ docType, document, projectId }) => {
  const isMounted = useRef(true);
  const [startDate, setStartDate] = useState(document?.startDate);
  const [endDate, setEndDate] = useState(document?.endDate);
  const [coaProjectId, setCoaProjectId] = useState(projectId);
  const [newFiles, setNewFiles] = useState<Record<FileHash, NewFileState>>({});
  // Already uploaded files, they are in AT (works in edit mode)
  const [attachments, setUploadedFiles] = useState(document?.attachments ?? []);
  const [isSaving, setIsSaving] = useState(false);

  const coaProjects = useDocumentsStore((state) => state.coaProjects);
  const setDocuments = useDocumentsStore((state) => state.setDocuments);
  const hideWindow = useAppStore((state) => state.hideWindow);
  const pilotApiService = getPilotApiService();

  const isEndDateMandatory = docType !== DocType.TAX_FORM_W9;
  const isCoaProject = docType === DocType.CERTIFICATE_OF_AUTHORIZATION;

  const isValid =
    startDate &&
    (isEndDateMandatory ? endDate : true) &&
    ((isEndDateMandatory && endDate) || !isEndDateMandatory) &&
    ((isCoaProject && coaProjectId) || !isCoaProject) &&
    (Object.values(newFiles).length > 0 || attachments.length > 0) &&
    !Object.values(newFiles).some((f) => f.isUploading);

  function handleCancelClick(isCloseClick?: boolean) {
    trackAction(`${isCloseClick ? 'Close' : 'Cancel'} ${document ? 'edit' : 'upload'} document button click`, null);
    hideWindow();
  }

  async function handleSaveClick() {
    setIsSaving(true);
    trackAction(`Save ${document ? 'edited' : 'uploaded'} document button click`, null);
    const startDateStr = dateToDateStr(startDate);
    const endDateStr = dateToDateStr(endDate);
    const fileUrls = Object.values(newFiles)
      .map((f) => f.exchangeUrl)
      .filter((url): url is string => Boolean(url));

    if (document) {
      await pilotApiService.updatePilotDocument({
        documentId: document.documentId,
        startDate: startDateStr,
        endDate: endDateStr,
        attachments: [...attachments.map(({ id }) => ({ id })), ...fileUrls.map((url) => ({ url }))],
      });
    } else {
      await pilotApiService.createPilotDocument({
        documentType: docType,
        startDate: startDateStr,
        endDate: endDateStr,
        fileUrls,
        coaProjectId,
      });
    }

    const docsResponse = await pilotApiService.getPilotDocuments();

    setDocuments(docsResponse);

    if (isMounted.current) {
      setIsSaving(false);
    }

    hideWindow();
  }

  function handleFilesOpen(newFiles: File[]) {
    newFiles.forEach(async (file) => {
      const fileHash = calcHash(file);

      if (!newFiles[fileHash]) {
        setNewFiles((curFiles) => ({ [fileHash]: { file, isUploading: true }, ...curFiles }));
        const exchangeUrl = await pilotApiService.uploadPilotAttachment(file, FileUploadContext.PilotDocuments);

        if (isMounted.current) {
          setNewFiles((curFiles) => {
            // the user can click delete file while it is uploading, so we need to check after uploading if file is still needed
            if (curFiles[fileHash]) {
              return { ...curFiles, [fileHash]: { file, exchangeUrl, isUploading: false } };
            }

            return curFiles;
          });
        }
      }
    });
  }

  function handleDeleteFile(fileHash: FileHash) {
    trackAction('Delete document file button click', null);

    setNewFiles(({ [fileHash]: _toDelete, ...restFiles }) => restFiles);
  }

  function handleDeleteAttachment(attachmentId: string) {
    trackAction('Delete document file button click', null);

    setUploadedFiles((curUploadedFiles) => curUploadedFiles.filter((f) => f.id !== attachmentId));
  }

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  return (
    <div className={css.root}>
      {!isSaving && (
        <WindowHeader
          title={`${document ? 'Edit' : 'Upload'} ${docType}`}
          onClose={() => handleCancelClick(true)}
          theme="light"
        />
      )}
      <WindowBody className={css.body}>
        {/* to save the container height  */}
        <div style={isSaving ? { visibility: 'hidden' } : undefined}>
          <div className={css.datePickerContainer}>
            <div className={css.datePickerItem}>
              <DocDatePicker date={startDate} onChange={setStartDate} placeholder="Start Date" />
            </div>
            {isEndDateMandatory && (
              <div className={css.datePickerItem}>
                <DocDatePicker date={endDate} onChange={setEndDate} placeholder="End Date" />
              </div>
            )}
          </div>
          {isCoaProject && (
            <Select
              placeholder="Select Project"
              label="Project Name"
              onChange={setCoaProjectId}
              value={coaProjectId}
              options={coaProjects.map((p) => ({ label: p.projectName, value: p.projectId }))}
              disabled={Boolean(document)}
            />
          )}
          <DocDropbox onDrop={handleFilesOpen} />
          {(Object.keys(newFiles).length > 0 || attachments?.length > 0) && (
            <div className={css.filesContainer}>
              {Object.entries(newFiles).map(([fileHash, fileState]) => (
                <FilePreview
                  key={fileHash}
                  fileName={fileState.file.name}
                  fileSize={fileState.file.size}
                  file={fileState.file}
                  isUploading={fileState.isUploading}
                  onDelete={() => handleDeleteFile(fileHash)}
                />
              ))}
              {attachments.map((attachment) => (
                <FilePreview
                  key={attachment.id}
                  fileName={attachment.filename}
                  fileSize={attachment.size}
                  onDelete={() => handleDeleteAttachment(attachment.id)}
                  thumbnailUrl={attachment.thumbnails?.large.url}
                  downloadUrl={attachment.url}
                />
              ))}
            </div>
          )}
          <ButtonsRow className={style({ gap: 8, marginTop: 32 })}>
            <Button onClick={() => handleCancelClick()} text="Cancel" style="secondary" className={css.button} />
            <Button onClick={handleSaveClick} text="Save" disabled={!isValid} className={css.button} />
          </ButtonsRow>
        </div>
        {isSaving && (
          <div className={css.loaderContainer}>
            <Loader className={style({ width: 42, height: 42 })} />
            <div className={classes(typeCompact14Medium, css.loaderText)}>Please wait...</div>
          </div>
        )}
      </WindowBody>
    </div>
  );
};

const css = stylesheet({
  root: {
    width: 320,
    maxHeight: 'calc(100vh - 120px)',
    borderRadius: 16,
    boxShadow: '0px 3px 20px 0px rgba(0, 0, 0, 0.20), 0px 6px 40px 0px rgba(0, 0, 0, 0.40)',
    overflowY: 'auto',
  },
  body: {
    padding: 16,
    position: 'relative',
  },
  datePickerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 16,
  },
  datePickerItem: {
    flex: '1 0 50%',
  },
  filesContainer: {
    marginTop: 8,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: 8,
  },
  button: {
    width: 140,
  },
  loaderContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  loaderText: {
    marginTop: 8,
    color: Gray100,
  },
});

function dateToDateStr(date?: Date) {
  return date ? format(date, 'yyyy-MM-dd') : undefined;
}

export default EditDocPopup;
